'use client';

import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useGatewayEvents } from '@/lib/useGatewayEvents';

type RuntimeStatus = {
  runtime: {
    selectedProject: string;
    workingDirectory: string;
    useCurrentRepositoryRoot: boolean;
    protectedPaths: string[];
  };
  services: {
    dashboard: { role: string; url: string };
    gateway: {
      role: string;
      pid: number;
      uptimeSeconds: number;
      hostname: string;
      apiBaseUrl: string;
    };
    worker: {
      managedByGateway: boolean;
      running: boolean;
      pid: number | null;
      url: string;
      health: 'unknown' | 'healthy' | 'unreachable';
      command: string;
      workingDirectory: string;
    };
  };
  companions?: Array<{
    id: string;
    role: string;
    installMethod: 'desktop-binary';
    installed: boolean;
    binaryPath: string;
    desktopEntryPath?: string;
    launchCommand: string;
    notes?: string;
  }>;
  flow: string[];
  recentLogs: Array<{
    timestamp: string;
    source: 'gateway' | 'worker';
    level: 'info' | 'warn' | 'error';
    message: string;
  }>;
};

const gatewayApi =
  (process.env.NEXT_PUBLIC_GATEWAY_URL || 'http://localhost:4100/api').replace(
    /\/$/,
    '',
  );

export default function RuntimeControlCard() {
  const [status, setStatus] = useState<RuntimeStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [workerAction, setWorkerAction] = useState<'start' | 'stop' | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [workingDirectory, setWorkingDirectory] = useState('');
  const [protectedPaths, setProtectedPaths] = useState('');
  const [useCurrentRepositoryRoot, setUseCurrentRepositoryRoot] =
    useState(true);

  async function fetchStatus() {
    try {
      const response = await fetch(`${gatewayApi}/runtime/status`);
      if (!response.ok) {
        throw new Error(`Gateway returned ${response.status}`);
      }

      const data: RuntimeStatus = await response.json();
      setStatus(data);
      setSelectedProject(data.runtime.selectedProject);
      setWorkingDirectory(data.runtime.workingDirectory);
      setProtectedPaths(data.runtime.protectedPaths.join('\n'));
      setUseCurrentRepositoryRoot(data.runtime.useCurrentRepositoryRoot);
      setError(null);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    }
  }

  useEffect(() => {
    fetchStatus();
  }, []);

  useGatewayEvents(
    ['runtime.status', 'project.context.updated', 'worker.status'],
    () => {
      void fetchStatus();
    },
  );

  useGatewayEvents(['runtime.log'], (event) => {
    setStatus((current) => {
      if (!current || !event.payload || typeof event.payload !== 'object') {
        return current;
      }

      const payload = event.payload as { level?: string; message?: string };
      const level: 'info' | 'warn' | 'error' =
        payload.level === 'warn' || payload.level === 'error'
          ? payload.level
          : 'info';
      return {
        ...current,
        recentLogs: [
          {
            timestamp: event.timestamp,
            source: event.source === 'dashboard' ? 'gateway' : event.source,
            level,
            message: payload.message || '',
          },
          ...current.recentLogs,
        ].slice(0, 20),
      };
    });
  });

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${gatewayApi}/runtime/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectedProject,
          workingDirectory,
          useCurrentRepositoryRoot,
          protectedPaths: protectedPaths
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean),
        }),
      });

      if (!response.ok) {
        throw new Error(`Save failed with ${response.status}`);
      }

      await fetchStatus();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setSaving(false);
    }
  }

  async function handleWorker(action: 'start' | 'stop') {
    setWorkerAction(action);

    try {
      const response = await fetch(`${gatewayApi}/runtime/worker/${action}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Worker ${action} failed with ${response.status}`);
      }

      await fetchStatus();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setWorkerAction(null);
    }
  }

  const workerTone = useMemo(() => {
    if (!status) {
      return '#6b7280';
    }
    if (status.services.worker.health === 'healthy') {
      return '#16a34a';
    }
    if (status.services.worker.running) {
      return '#d97706';
    }
    return '#dc2626';
  }, [status]);

  if (!status) {
    return (
      <div style={cardStyle}>
        <h2 style={titleStyle}>Runtime Control</h2>
        <p style={mutedStyle}>Loading runtime status...</p>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={heroStyle}>
        <div>
          <div style={eyebrowStyle}>Dev Flow</div>
          <h2 style={titleStyle}>Dashboard - Gateway - Worker</h2>
          <p style={bodyStyle}>
            Next.js now acts as the frontend monitor. Runtime control lives in
            Nest, and AI execution is delegated to the Python worker.
          </p>
        </div>
        <div style={{ ...badgeStyle, borderColor: workerTone, color: workerTone }}>
          {status.services.worker.running ? 'Worker Running' : 'Worker Stopped'}
        </div>
      </div>

      {error ? <div style={errorStyle}>{error}</div> : null}

      <div style={gridStyle}>
        <section style={panelStyle}>
          <h3 style={sectionTitleStyle}>Flow</h3>
          {status.flow.map((step) => (
            <div key={step} style={pillStyle}>
              {step}
            </div>
          ))}
        </section>

        <section style={panelStyle}>
          <h3 style={sectionTitleStyle}>Services</h3>
          <div style={kvRowStyle}>
            <span>Gateway PID</span>
            <strong>{status.services.gateway.pid}</strong>
          </div>
          <div style={kvRowStyle}>
            <span>Gateway Host</span>
            <strong>{status.services.gateway.hostname}</strong>
          </div>
          <div style={kvRowStyle}>
            <span>Worker PID</span>
            <strong>{status.services.worker.pid ?? 'not running'}</strong>
          </div>
          <div style={kvRowStyle}>
            <span>Worker Health</span>
            <strong style={{ color: workerTone }}>
              {status.services.worker.health}
            </strong>
          </div>
          <div style={kvRowStyle}>
            <span>Worker URL</span>
            <strong>{status.services.worker.url}</strong>
          </div>
        </section>

        <section style={panelStyle}>
          <h3 style={sectionTitleStyle}>Companions</h3>
          {(status.companions || []).length === 0 ? (
            <p style={mutedStyle}>No optional companions registered.</p>
          ) : (
            status.companions?.map((companion) => (
              <div key={companion.id} style={{ display: 'grid', gap: '0.5rem' }}>
                <div style={kvRowStyle}>
                  <span>{companion.id}</span>
                  <strong style={{ color: companion.installed ? '#16a34a' : '#d97706' }}>
                    {companion.installed ? 'installed' : 'not installed'}
                  </strong>
                </div>
                <div style={miniMetaStyle}>{companion.role}</div>
                <div style={miniMetaStyle}>{companion.binaryPath}</div>
                <div style={miniMetaStyle}>{companion.launchCommand}</div>
              </div>
            ))
          )}
        </section>
      </div>

      <form onSubmit={handleSave} style={formStyle}>
        <div style={formHeaderStyle}>
          <h3 style={sectionTitleStyle}>Runtime Configuration</h3>
          <button disabled={saving} type="submit" style={primaryButtonStyle}>
            {saving ? 'Saving...' : 'Save Config'}
          </button>
        </div>

        <div style={formGridStyle}>
          <label style={labelStyle}>
            Project
            <input
              value={selectedProject}
              onChange={(event) => setSelectedProject(event.target.value)}
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            Working Directory
            <input
              value={workingDirectory}
              onChange={(event) => setWorkingDirectory(event.target.value)}
              style={inputStyle}
            />
          </label>
        </div>

        <label style={{ ...labelStyle, marginTop: 12 }}>
          Protected Paths
          <textarea
            rows={5}
            value={protectedPaths}
            onChange={(event) => setProtectedPaths(event.target.value)}
            style={textareaStyle}
          />
        </label>

        <label style={checkboxRowStyle}>
          <input
            checked={useCurrentRepositoryRoot}
            onChange={(event) => setUseCurrentRepositoryRoot(event.target.checked)}
            type="checkbox"
          />
          Use current repository root as default workspace
        </label>
      </form>

      <div style={actionsStyle}>
        <button
          disabled={workerAction !== null}
          onClick={() => handleWorker('start')}
          style={primaryButtonStyle}
          type="button"
        >
          {workerAction === 'start' ? 'Starting...' : 'Start Worker'}
        </button>
        <button
          disabled={workerAction !== null}
          onClick={() => handleWorker('stop')}
          style={secondaryButtonStyle}
          type="button"
        >
          {workerAction === 'stop' ? 'Stopping...' : 'Stop Worker'}
        </button>
      </div>

      <section style={{ ...panelStyle, marginTop: 20 }}>
        <h3 style={sectionTitleStyle}>Recent Logs</h3>
        <div style={logsStyle}>
          {status.recentLogs.length === 0 ? (
            <div style={mutedStyle}>No logs yet.</div>
          ) : (
            status.recentLogs.slice(0, 18).map((entry) => (
              <div key={`${entry.timestamp}-${entry.message}`} style={logRowStyle}>
                <span style={logMetaStyle}>
                  [{new Date(entry.timestamp).toLocaleTimeString()}] {entry.source}
                </span>
                <span>{entry.message}</span>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background:
    'linear-gradient(180deg, rgba(247,248,245,1) 0%, rgba(255,255,255,1) 100%)',
  border: '1px solid #d6d3d1',
  borderRadius: 24,
  boxShadow: '0 18px 50px rgba(28, 25, 23, 0.08)',
  padding: 24,
};

const heroStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  gap: 16,
  justifyContent: 'space-between',
  marginBottom: 20,
};

const eyebrowStyle: React.CSSProperties = {
  color: '#9a3412',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.12em',
  marginBottom: 6,
  textTransform: 'uppercase',
};

const titleStyle: React.CSSProperties = {
  color: '#1c1917',
  fontSize: 28,
  fontWeight: 700,
  margin: 0,
};

const bodyStyle: React.CSSProperties = {
  color: '#57534e',
  fontSize: 14,
  lineHeight: 1.6,
  margin: '8px 0 0 0',
  maxWidth: 640,
};

const badgeStyle: React.CSSProperties = {
  background: '#fafaf9',
  border: '2px solid #d6d3d1',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 700,
  padding: '10px 16px',
  whiteSpace: 'nowrap',
};

const errorStyle: React.CSSProperties = {
  background: '#fef2f2',
  border: '1px solid #fca5a5',
  borderRadius: 16,
  color: '#991b1b',
  marginBottom: 16,
  padding: 12,
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gap: 16,
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  marginBottom: 20,
};

const panelStyle: React.CSSProperties = {
  background: '#fafaf9',
  border: '1px solid #e7e5e4',
  borderRadius: 18,
  padding: 16,
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#292524',
  fontSize: 16,
  fontWeight: 700,
  margin: '0 0 12px 0',
};

const pillStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #d6d3d1',
  borderRadius: 999,
  color: '#44403c',
  fontSize: 13,
  marginBottom: 8,
  padding: '8px 12px',
};

const kvRowStyle: React.CSSProperties = {
  alignItems: 'center',
  color: '#57534e',
  display: 'flex',
  fontSize: 13,
  justifyContent: 'space-between',
  marginBottom: 10,
};

const miniMetaStyle: React.CSSProperties = {
  color: '#78716c',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 12,
  overflowWrap: 'anywhere',
};

const formStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e7e5e4',
  borderRadius: 18,
  padding: 16,
};

const formHeaderStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 16,
};

const formGridStyle: React.CSSProperties = {
  display: 'grid',
  gap: 12,
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
};

const labelStyle: React.CSSProperties = {
  color: '#44403c',
  display: 'flex',
  flexDirection: 'column',
  fontSize: 13,
  fontWeight: 600,
  gap: 8,
};

const inputStyle: React.CSSProperties = {
  background: '#fafaf9',
  border: '1px solid #d6d3d1',
  borderRadius: 12,
  color: '#1c1917',
  fontSize: 14,
  padding: '10px 12px',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};

const checkboxRowStyle: React.CSSProperties = {
  alignItems: 'center',
  color: '#44403c',
  display: 'flex',
  fontSize: 13,
  gap: 10,
  marginTop: 14,
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  marginTop: 20,
};

const primaryButtonStyle: React.CSSProperties = {
  background: '#1d4ed8',
  border: 'none',
  borderRadius: 999,
  color: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 700,
  padding: '10px 16px',
};

const secondaryButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  background: '#e7e5e4',
  color: '#1c1917',
};

const logsStyle: React.CSSProperties = {
  background: '#1c1917',
  borderRadius: 16,
  color: '#f5f5f4',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 12,
  maxHeight: 280,
  overflow: 'auto',
  padding: 14,
};

const logRowStyle: React.CSSProperties = {
  borderBottom: '1px solid rgba(245, 245, 244, 0.08)',
  marginBottom: 8,
  padding: '0 0 8px 0',
};

const logMetaStyle: React.CSSProperties = {
  color: '#fbbf24',
  display: 'block',
  marginBottom: 4,
};

const mutedStyle: React.CSSProperties = {
  color: '#78716c',
  fontSize: 14,
  margin: 0,
};
